import React, { useEffect, useMemo, useState } from 'react';
import { Button, ButtonGroup, Col, Offcanvas, Placeholder, Row } from 'react-bootstrap';
import { ChevronRight } from 'lucide-react';
import { ImageOverlay, MapContainer, Marker, Polygon, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { LAYER_ORDER, boundsToLeaflet, findingsFor, imageSourceFrom, patchAdvice, spotAdvice } from './satelliteModel';
import { classAt } from './satellitePatches';
import { classLabel, layerLabel, renderSatText, satDate, t, toneColour, toneWord } from './satelliteText';

const ESRI_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const ESRI_ATTR = 'Imagery © Esri, Maxar, Earthstar Geographics';

/** CropGen's picture of one layer as a map overlay, or null without a usable one. */
const overlayFor = (entry) => {
  const data = entry && entry.status === 'ready' ? entry.data : null;
  if (!data || !data.imageBase64 || !data.bounds) return null;
  const url = imageSourceFrom(data.imageBase64);
  const bounds = boundsToLeaflet(data.bounds);
  return url && bounds ? { url, bounds } : null;
};

/** Frames the field once per farm. */
function FitField({ farm }) {
  const map = useMap();
  useEffect(() => {
    if (!farm.bounds) return;
    const { north, south, east, west } = farm.bounds;
    map.fitBounds(
      [
        [south, west],
        [north, east],
      ],
      { padding: [24, 24], maxZoom: 18, animate: false }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, farm.id]);
  return null;
}

const letterIcon = (letter, tone, selected) =>
  L.divIcon({
    className: '',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    html: `<span style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:${toneColour(
      tone
    )};color:#fff;font:700 13px/1 sans-serif;border:${selected ? 3 : 2}px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.45)">${letter}</span>`,
  });

/** A patch's name: "Patch A · north-west corner". */
const patchName = (patch) =>
  renderSatText({ key: 'patchNameNoArea', params: { letter: patch.letter, placeKey: patch.placeKey } });

/**
 * Every layer's reading at the patch: its own picture's class for the patch,
 * and each other picture's class at the patch's centre. A picture that could
 * not be read there is left out, not guessed. (farmer-app PatchSheet.readingsAt)
 */
const readingsAt = (patch, entries, pixels) =>
  LAYER_ORDER.map((id) => {
    if (id === patch.signal) {
      return { id, label: patch.topClass, tone: patch.tone };
    }
    const entry = entries[id];
    const data = entry && entry.status === 'ready' ? entry.data : null;
    const cls = data ? classAt(pixels[id], entry.classified, data.bounds, patch.centre) : null;
    if (!cls) return null;
    const advice = spotAdvice(cls, id);
    return { id, label: cls.label, tone: advice ? advice.tone : cls.tone };
  }).filter((r) => r && r.label);

function PatchDetail({ patch, entries, pixels, onClose }) {
  const advice = patchAdvice(patch);
  const entry = entries[patch.signal];
  const date = entry && entry.status === 'ready' ? entry.data.date : null;
  const share = Number.isFinite(patch.acres)
    ? t('mapShareOfField', { pct: Math.max(1, Math.round(patch.sharePct)), acres: patch.acres.toFixed(2) })
    : t('mapShareOfFieldNoArea', { pct: Math.max(1, Math.round(patch.sharePct)) });
  const meta = [share, layerLabel(patch.signal), date ? satDate(date) : null].filter(Boolean).join(' · ');
  const readings = readingsAt(patch, entries, pixels);

  return (
    <Offcanvas show onHide={onClose} placement="end">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="d-flex align-items-center gap-2">
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
            style={{ width: 32, height: 32, background: toneColour(patch.tone) }}
          >
            {patch.letter}
          </span>
          <span>{patchName(patch)}</span>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {/* justifyContent inline: styles/reports.css stacks every `.d-flex.justify-content-between` on phones. */}
        <div className="d-flex align-items-start gap-2 mb-3" style={{ justifyContent: 'space-between' }}>
          <small className="text-muted">{meta}</small>
          <span className="badge" style={{ background: toneColour(patch.tone) }}>
            {toneWord(patch.tone).toLocaleUpperCase()}
          </span>
        </div>

        <h6 className="text-uppercase text-muted small fw-bold">{t('mfRightNow')}</h6>
        <ul className="list-group mb-2">
          {readings.map((r) => (
            <li key={r.id} className="list-group-item d-flex align-items-center" style={{ justifyContent: 'space-between' }}>
              <span className="fw-semibold">{layerLabel(r.id)}</span>
              <span className="text-end">
                <span className="fw-semibold">{classLabel(r.label)}</span>
                <small className="d-block fw-bold" style={{ color: toneColour(r.tone) }}>
                  {toneWord(r.tone).toLocaleUpperCase()}
                </small>
              </span>
            </li>
          ))}
        </ul>
        {readings.length > 1 && <small className="text-muted d-block mb-3">{t('mfReadingsNote')}</small>}

        <h6 className="text-uppercase text-muted small fw-bold mt-3">{t('mfWhatToDoHere')}</h6>
        {advice && advice.todo && <p className="fw-semibold">{renderSatText(advice.todo)}</p>}

        <Button variant="outline-secondary" onClick={onClose}>
          {t('mapClose')}
        </Button>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

/**
 * "What the satellite found": the four layers as tabs, the field on the map
 * with CropGen's picture of the chosen one and a lettered marker on each
 * named patch, and what the pictures show, patch by patch. A patch opens from
 * its marker or its row. (farmer-app SatelliteFoundCard + FarmHeroMap + PatchSheet)
 */
export default function SatelliteFoundSection({ farm, entries, signals, settledSignals, patches, pixels, failed, onRetry }) {
  const [layer, setLayer] = useState(LAYER_ORDER[0]);
  const [openPatch, setOpenPatch] = useState(null);

  useEffect(() => {
    setLayer(LAYER_ORDER[0]);
    setOpenPatch(null);
  }, [farm.id]);

  const entry = entries[layer];
  const loading = !entry || entry.status === 'loading';
  const overlay = useMemo(() => overlayFor(entry), [entry]);
  const signal = signals[layer];
  const findings = useMemo(() => findingsFor(LAYER_ORDER, settledSignals, patches), [settledSignals, patches]);
  const markers = overlay ? patches.filter((p) => p.signal === layer && p.centre) : [];
  const ring = farm.ring.map((p) => [p.lat, p.lng]);

  const showPatch = (patch) => {
    setLayer(patch.signal);
    setOpenPatch(patch);
  };

  return (
    <>
      <ButtonGroup className="mb-2 flex-wrap" aria-label="Satellite layer">
        {LAYER_ORDER.map((id) => (
          <Button
            key={id}
            size="sm"
            variant={layer === id ? 'primary' : 'outline-primary'}
            onClick={() => {
              setLayer(id);
              setOpenPatch(null);
            }}
          >
            {layerLabel(id)}
          </Button>
        ))}
      </ButtonGroup>

      <Row className="g-3">
        <Col lg={7}>
          <div className="position-relative rounded overflow-hidden border" style={{ height: 460 }}>
            <MapContainer
              center={farm.centre ? [farm.centre.lat, farm.centre.lng] : [20.5937, 78.9629]}
              zoom={16}
              style={{ width: '100%', height: '100%' }}
              scrollWheelZoom
            >
              <TileLayer url={ESRI_URL} attribution={ESRI_ATTR} maxNativeZoom={18} maxZoom={20} />
              {overlay && <ImageOverlay url={overlay.url} bounds={overlay.bounds} opacity={0.78} />}
              <Polygon
                positions={ring}
                pathOptions={{ color: '#2D7D46', weight: 2, fillColor: '#2D7D46', fillOpacity: overlay ? 0 : 0.22 }}
              />
              {markers.map((p) => (
                <Marker
                  key={p.key}
                  position={[p.centre.lat, p.centre.lng]}
                  icon={letterIcon(p.letter, p.tone, openPatch && openPatch.key === p.key)}
                  eventHandlers={{ click: () => showPatch(p) }}
                />
              ))}
              <FitField farm={farm} />
            </MapContainer>
            {(loading || !overlay) && (
              <span
                className="position-absolute bottom-0 start-0 m-2 px-2 py-1 rounded bg-dark text-white small"
                style={{ zIndex: 500, opacity: 0.85 }}
                aria-live="polite"
              >
                {loading ? t('mfPictureLoading') : signal && signal.plain ? renderSatText(signal.plain) : t('sigNoReading')}
              </span>
            )}
          </div>
        </Col>

        <Col lg={5}>
          <div className="d-flex flex-wrap gap-3 align-items-center small mb-2">
            {['good', 'watch', 'bad'].map((tone) => (
              <span key={tone} className="d-inline-flex align-items-center gap-1">
                <i className="d-inline-block rounded-circle" style={{ width: 10, height: 10, background: toneColour(tone) }} />
                {t({ good: 'mfToneGoodWord', watch: 'mfToneWatchWord', bad: 'mfToneBadWord' }[tone])}
              </span>
            ))}
            <span className="text-muted ms-auto">Click a patch for details</span>
          </div>

          {failed && (
            <div className="alert alert-warning d-flex align-items-center gap-2 py-2" style={{ justifyContent: 'space-between' }}>
              <span>{t('satLoadFailed')}</span>
              <Button size="sm" variant="outline-dark" onClick={onRetry}>
                {t('satRetry')}
              </Button>
            </div>
          )}

          {!findings.length && (
            <Placeholder as="div" animation="glow">
              <Placeholder xs={12} className="mb-2" />
              <Placeholder xs={8} />
            </Placeholder>
          )}
          <div className="list-group">
            {findings.map((row) => {
              const todo = row.patch ? patchAdvice(row.patch)?.todo : row.todo;
              const body = renderSatText(todo);
              return (
                <button
                  key={row.key}
                  type="button"
                  className="list-group-item list-group-item-action d-flex align-items-center gap-2 text-start"
                  onClick={() => (row.patch ? showPatch(row.patch) : setLayer(row.layer))}
                >
                  <i
                    className="d-inline-block rounded-circle flex-shrink-0"
                    style={{ width: 10, height: 10, background: toneColour(row.tone) }}
                    aria-hidden="true"
                  />
                  <span className="flex-grow-1">
                    <span className="d-block fw-semibold">{row.patch ? patchName(row.patch) : renderSatText(row.text)}</span>
                    <small className="text-muted">
                      <span className="fw-semibold">{layerLabel(row.layer)}</span>
                      {body ? ` · ${body}` : ''}
                    </small>
                  </span>
                  <ChevronRight size={16} className="text-muted flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </Col>
      </Row>

      {openPatch && <PatchDetail patch={openPatch} entries={entries} pixels={pixels} onClose={() => setOpenPatch(null)} />}
    </>
  );
}
